import { mapboxToken } from "@/constants";
import { BAM_MAP_STYLE_SOURCES } from "@/constants/mapStyles";
import {
  selectPOIById,
  selectPOICategories,
  selectVisiblePOIs,
} from "@/features/survey/poisSelectors";
import { updateCategoryField } from "@/features/survey/poisSlice";
import {
  selectCurrentSurveyId,
  selectMarketMapKeys,
} from "@/features/survey/surveySelectors";
import * as mapUtil from "@/shared/utils/map";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldIconButton } from "@emerald-react/icon-button";
import { EmeraldInput } from "@emerald-react/input";
import { EmeraldInputV2 } from "@emerald-react/input-v2";
import { EmeraldButtonSize, EmeraldButtonVariant } from "@emerald/nxcore";
import { usePoiCategoryWarning } from "@hooks/usePoiCategoryWarning";
import { usePoiFormField } from "@hooks/usePoiFormField";
import {
  useCurrentPoiId,
  useIsPOIsDrawerOpen,
} from "@hooks/useSurveySelectors";
import { useFetchGeocodeAddressMutation } from "@services/survey-core";
import { useUpdateSurveyMapKeyMutation } from "@services/survey-mapKeys";
import { useUpdatePOICategoryMutation } from "@services/survey-poi";
import MapOverlay from "@survey-components/properties/MapOverlay";
import type {
  SurveyPOICategory,
  SurveyPOIRequest,
} from "@survey-types/survey.poi.types";
import {
  currentPoiId as currentPoiIdAction,
  setOpenPOIsDrawer,
  updateToggleLabelMapKey,
} from "@survey/surveySlice";
import debounce from "lodash/debounce";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  BaseSyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CategoryCreationModal from "./CategoryCreationModal";
import CategorySelectWithRename from "./CategorySelectWithRename";
import PoiDesignForm from "./PoiDesignForm";
import RenameCategoryModal from "./RenameCategoryModal";

const defaultFormValues: SurveyPOIRequest = {
  categoryId: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: "",
  latitude: "",
  longitude: "",
  locationNotes: "",
};

const PoiDetailForm = () => {
  const dispatch = useAppDispatch();
  const surveyId = useAppSelector(selectCurrentSurveyId);
  const currentPoiId = useCurrentPoiId();
  const isPOIsDrawerOpen = useIsPOIsDrawerOpen();

  // Get current POI from Redux
  const currentPOI = useAppSelector((state) =>
    selectPOIById(currentPoiId)(state),
  );

  // Use shared hook for POI category warning logic
  const {
    showCategoryWarning,
    confirmCategoryWarning,
    checkAndDeletePOIWithoutCategory,
  } = usePoiCategoryWarning();

  // Form field management: focus tracking, validation, auto-save with optimistic updates.
  // Extracted to hook for reusability and maintainability. See usePoiFormField.ts for details.
  const {
    formErrors,
    getFieldValue,
    handleFocus,
    handleInputChange,
    handleBlur,
  } = usePoiFormField({ currentPOI, surveyId: surveyId ?? null });

  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapAddressContainerRef = useRef<HTMLDivElement | null>(null);
  const [formValues, setFormValues] =
    useState<SurveyPOIRequest>(defaultFormValues);
  const formValuesRef = useRef(formValues);
  formValuesRef.current = formValues;
  const [coordinates, setCoordinates] = useState<[string, string]>(["", ""]);
  const [isMapReady, setIsMapReady] = useState(false);
  const [isLatInvalid, setIsLatInvalid] = useState(false);
  const [isLngInvalid, setIsLngInvalid] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryBeforeCreateModal, setCategoryBeforeCreateModal] =
    useState("");
  const [categorySelectResetKey, setCategorySelectResetKey] = useState(0);
  const [newlyCreatedCategories, setNewlyCreatedCategories] = useState<
    SurveyPOICategory[]
  >([]);
  const [showRenameCategoryModal, setShowRenameCategoryModal] = useState(false);
  const [categoryToRename, setCategoryToRename] =
    useState<SurveyPOICategory | null>(null);

  // Derive warning state using shared hook
  const showWarning = showCategoryWarning(formValues.categoryId);

  const [updatePOICategory, { isLoading: isRenamingCategory }] =
    useUpdatePOICategoryMutation();
  const [updateSurveyMapKey] = useUpdateSurveyMapKeyMutation();
  const marketMapKeys = useAppSelector(selectMarketMapKeys);

  const [fetchGeocodeAddress] = useFetchGeocodeAddressMutation();
  // POI categories are fetched at Builder.tsx level to avoid duplicate requests
  // Use Redux state for rendering (automatically updated by the top-level query via onQueryStarted)
  const categories = useAppSelector(selectPOICategories);

  // Combine newly created categories with fetched categories (no duplicates)
  const allCategories = useMemo(
    () => [
      ...newlyCreatedCategories,
      ...categories.filter(
        (cat) =>
          !newlyCreatedCategories.some((newCat) => newCat.extId === cat.extId),
      ),
    ],
    [categories, newlyCreatedCategories],
  );

  const categoryOptions = useMemo(() => {
    return [
      ...allCategories.map((category) => ({
        value: category.extId,
        label: category.categoryName,
      })),
      // Special "create new" option at bottom
      {
        value: "__CREATE_NEW__",
        label: "+ Create New Category",
      },
    ];
  }, [allCategories]);

  const updateMapView = useCallback(
    (newCenter: [number, number], newZoom: number) => {
      mapRef.current?.setCenter(newCenter);
      mapRef.current?.setZoom(newZoom || 2.5);
    },
    [],
  );

  const isValidCoordinatePair = useCallback(
    (lngValue: string, latValue: string) => {
      return mapUtil.isValidCoordinates([Number(lngValue), Number(latValue)]);
    },
    [],
  );

  const updateCoordinateErrors = useCallback(
    (latValue: string, lngValue: string) => {
      const parsedLatitude = Number(latValue);
      const parsedLongitude = Number(lngValue);

      const latitudeInvalid =
        latValue.trim() !== "" &&
        (Number.isNaN(parsedLatitude) ||
          parsedLatitude < -90 ||
          parsedLatitude > 90 ||
          parsedLatitude === 0);
      const longitudeInvalid =
        lngValue.trim() !== "" &&
        (Number.isNaN(parsedLongitude) ||
          parsedLongitude < -180 ||
          parsedLongitude > 180 ||
          parsedLongitude === 0);

      setIsLatInvalid(latitudeInvalid);
      setIsLngInvalid(longitudeInvalid);
    },
    [],
  );

  const renderMapPoints = useCallback(
    (
      lngValue: string,
      latValue: string,
      addressData?: {
        address1?: string;
        city?: string;
        stateProvince?: string;
        postalCode?: string;
      },
    ) => {
      if (!mapRef.current) {
        return;
      }

      const parsedCoordinates: [number, number] = [
        Number(lngValue),
        Number(latValue),
      ];

      mapUtil.deleteMarkers(mapRef.current);

      if (mapUtil.isValidCoordinates(parsedCoordinates)) {
        const points = mapUtil.getPointsForAddressForm(
          parsedCoordinates,
          addressData || {},
          {
            pinType: currentPOI?.category?.pinType || "ICON",
            pointSize: currentPOI?.category?.pointSize || 30,
            iconData: currentPOI?.category?.iconData || { color: "#a23f97" },
            logoUrl:
              currentPOI?.category?.logoData?.logoLinks?.find(
                (l) => l.isActive !== false,
              )?.url ?? currentPOI?.category?.logoData?.logoLinks?.[0]?.url,
            logoWhiteBackground:
              currentPOI?.category?.logoData?.logoWhiteBackground || false,
          },
        );
        mapUtil.getMarkerConfig(points, mapRef.current);
        updateMapView(parsedCoordinates, 12);
        return;
      }

      updateMapView([-95, 40], 2.5);
    },
    [updateMapView, currentPOI?.category],
  );

  const fetchCoords = (payload: Record<string, unknown>) => {
    return fetchGeocodeAddress(JSON.stringify(payload))
      .then((response) => response?.data?.features?.[0]?.geometry?.coordinates)
      .catch((_error) => {
        console.error("Error fetching coordinates:");
        return null;
      });
  };

  // Not wrapped in useCallback - recreates with fresh closure on each render (like Lease Comp form)
  const geocodeAddress = (values: SurveyPOIRequest) => {
    const { addressLine1, city, state, postalCode, latitude, longitude } =
      values;

    // Only geocode if ALL these conditions are met (matches Lease Comps logic):
    // 1. No valid coordinates exist
    // 2. Address exists and is at least 2 chars
    // 3. EITHER (City + State both 2+ chars) OR (Zip code 5+ chars)
    if (
      (!Number(latitude) || !Number(longitude)) &&
      addressLine1 &&
      addressLine1.length > 1 &&
      ((city && city.length > 1 && state && state.length > 1) ||
        (postalCode && postalCode.length >= 5))
    ) {
      const payload = {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: {
              Address: addressLine1,
              City: city,
              StateProv: state,
            },
          },
        ],
      };

      fetchCoords(payload)
        .then((coords) => {
          const hasValidCoords =
            !!coords?.length &&
            mapUtil.isValidCoordinates([Number(coords[0]), Number(coords[1])]);

          if (!hasValidCoords) {
            updateMapView([-95, 40], 2.5);
            return;
          }

          const [nextLongitude, nextLatitude] = coords;
          const latitudeString = String(nextLatitude);
          const longitudeString = String(nextLongitude);

          // Update local state
          setFormValues((prev) => ({
            ...prev,
            latitude: latitudeString,
            longitude: longitudeString,
          }));
          setCoordinates([longitudeString, latitudeString]);
          updateCoordinateErrors(latitudeString, longitudeString);
          renderMapPoints(longitudeString, latitudeString, {
            address1: addressLine1,
            city,
            stateProvince: state,
            postalCode,
          });

          // Auto-save geocoded coordinates to backend using hook
          if (surveyId && currentPOI) {
            // Use hook's handleBlur to save coordinates (programmatic flag preserves user focus)
            handleBlur("latitude", latitudeString, true);
            handleBlur("longitude", longitudeString, true);
          }
        })
        .catch((_error) => {
          console.error("Error fetching coordinates:");
        });
    }
  };

  // Ref always points to the latest geocodeAddress so the stable debounce
  // below never captures a stale closure (which would lose currentPOI?.category).
  const geocodeAddressRef = useRef(geocodeAddress);
  geocodeAddressRef.current = geocodeAddress;

  // Stable debounce with empty deps (like Lease Comp form)
  const debouncedGeocodeAddress = useMemo(() => {
    return debounce((values: SurveyPOIRequest) => {
      geocodeAddressRef.current(values);
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Populate form when POI ID changes (not on every data update)
  // This is a valid pattern for syncing external state (Redux) to component state
  useEffect(() => {
    if (!currentPOI) {
      return;
    }

    // Populate form with POI data (works for both new and existing POIs)
    const poiFormValues: SurveyPOIRequest = {
      categoryId: currentPOI.categoryId || "",
      addressLine1: currentPOI.addressLine1 || "",
      addressLine2: currentPOI.addressLine2 || "",
      city: currentPOI.city || "",
      state: currentPOI.state || "",
      postalCode: currentPOI.postalCode || "",
      latitude: currentPOI.latitude || "",
      longitude: currentPOI.longitude || "",
      locationNotes: currentPOI.locationNotes || "",
    };

    setFormValues(poiFormValues);

    const lng = currentPOI.longitude || "";
    const lat = currentPOI.latitude || "";
    setCoordinates([lng, lat]);

    if (!isMapReady) {
      return;
    }

    // Update map
    if (isValidCoordinatePair(lng, lat)) {
      updateCoordinateErrors(lat, lng);
      renderMapPoints(lng, lat, {
        address1: currentPOI.addressLine1 || "",
        city: currentPOI.city || "",
        stateProvince: currentPOI.state || "",
        postalCode: currentPOI.postalCode || "",
      });
    } else {
      updateMapView([-95, 40], 2.5);
    }
  }, [currentPoiId, isMapReady]);

  useEffect(() => {
    mapboxgl.accessToken = mapboxToken || "";

    if (!mapAddressContainerRef.current) {
      return;
    }

    setIsMapReady(false);

    mapRef.current = new mapboxgl.Map({
      container: mapAddressContainerRef.current,
      style: BAM_MAP_STYLE_SOURCES.cbreLight,
      center: [-95, 40],
      zoom: 2.5,
      attributionControl: false,
    });

    if (mapRef.current.isStyleLoaded()) {
      setIsMapReady(true);
    } else {
      mapRef.current.once("load", () => {
        setIsMapReady(true);
      });
    }

    mapRef.current.resize();

    return () => {
      debouncedGeocodeAddress.cancel();
      setIsMapReady(false);
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, [debouncedGeocodeAddress]);

  // Clean up newly created categories once they're in the main categories array
  // This is a valid cleanup pattern for temporary optimistic state
  useEffect(() => {
    if (newlyCreatedCategories.length > 0 && categories.length > 0) {
      setNewlyCreatedCategories((prev) =>
        prev.filter(
          (newCat) => !categories.some((cat) => cat.extId === newCat.extId),
        ),
      );
    }
  }, [categories, newlyCreatedCategories]);

  useEffect(() => {
    const { longitude, latitude, addressLine1, city, state, postalCode } =
      formValuesRef.current;
    renderMapPoints(longitude || "", latitude || "", {
      address1: addressLine1 || "",
      city: city || "",
      stateProvince: state || "",
      postalCode: postalCode || "",
    });
  }, [
    currentPOI?.category?.pinType,
    currentPOI?.category?.iconData,
    currentPOI?.category?.logoData,
    renderMapPoints,
  ]);

  // Persist address fields that changed in local state but haven't been saved yet.
  // This covers browser autofill: onChange may fire for multiple fields while
  // onBlur only fires for the focused element.
  useEffect(() => {
    if (!currentPOI || !surveyId) {
      return;
    }

    const fields = ["addressLine1", "city", "state", "postalCode"] as const;

    const changedFields = fields.filter((field) => {
      const current = formValues[field] || "";
      const original = currentPOI[field] || "";
      return current.trim() !== "" && current !== original;
    });

    if (changedFields.length === 0) {
      return;
    }

    const timeoutId = setTimeout(() => {
      changedFields.forEach((field) => {
        const value = formValues[field] || "";
        if (value.trim() !== "") {
          handleBlur(field, value, true);
        }
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    surveyId,
    currentPOI?.extId,
    currentPOI?.addressLine1,
    currentPOI?.city,
    currentPOI?.state,
    currentPOI?.postalCode,
    formValues.addressLine1,
    formValues.city,
    formValues.state,
    formValues.postalCode,
  ]);

  // All visible POIs for record navigation
  const allPOIs = useAppSelector(selectVisiblePOIs);
  const poiNavIndex = allPOIs.findIndex((p) => p.extId === currentPoiId);
  const poiNavTotal = allPOIs.length;
  const poiHasPrev = poiNavIndex > 0;
  const poiHasNext = poiNavIndex < poiNavTotal - 1;

  const navigateToPOI = (index: number) => {
    const target = allPOIs[index];
    if (target) {
      dispatch(currentPoiIdAction({ poiId: target.extId }));
    }
  };

  if (!isPOIsDrawerOpen || !currentPOI) {
    return null;
  }

  const handleBackToList = async () => {
    if (showWarning && surveyId && currentPOI) {
      const userConfirmed = await confirmCategoryWarning();

      if (!userConfirmed) {
        return; // User chose "Stay Here"
      }

      await checkAndDeletePOIWithoutCategory(
        formValues.categoryId,
        surveyId,
        currentPOI.extId,
        currentPOI,
      );
    }

    // Return to POI list
    dispatch(currentPoiIdAction(null));
  };

  const handleCloseDrawer = async () => {
    if (showWarning && surveyId && currentPOI) {
      const userConfirmed = await confirmCategoryWarning();

      if (!userConfirmed) {
        return; // User chose "Stay Here"
      }

      await checkAndDeletePOIWithoutCategory(
        formValues.categoryId,
        surveyId,
        currentPOI.extId,
        currentPOI,
      );
    }

    // Close entire drawer
    dispatch(setOpenPOIsDrawer(false));
    dispatch(currentPoiIdAction(null));
  };

  const handleCategoryChange = (value: string) => {
    if (value === "__CREATE_NEW__") {
      setCategoryBeforeCreateModal(formValues.categoryId || "");
      setShowCategoryModal(true);
      return;
    }
    handleChange("categoryId", value);
    // Immediate save on category change
    handleBlur("categoryId", value);
  };

  const handleCategoryCreated = async (newCategory: SurveyPOICategory) => {
    // Optimistically add to local state so it's immediately in the dropdown options
    setNewlyCreatedCategories((prev) => [...prev, newCategory]);

    // Close the modal
    setShowCategoryModal(false);

    // Save to backend (which will update Redux)
    await handleBlur("categoryId", newCategory.extId);

    // Manually sync formValues with the new category ID
    // (The useEffect at line 357 won't fire because it only depends on currentPoiId changes)
    setFormValues((prev) => ({
      ...prev,
      categoryId: newCategory.extId,
    }));

    // Small delay to let React process the state update
    await new Promise((resolve) => setTimeout(resolve, 10));

    // Force remount to ensure EmeraldSelect displays the new value
    setCategorySelectResetKey((prev) => prev + 1);
  };

  const handleCloseCategoryModal = () => {
    // Restore previously selected category when user cancels creation.
    setFormValues((prev) => ({
      ...prev,
      categoryId: categoryBeforeCreateModal,
    }));
    setShowCategoryModal(false);
    setCategorySelectResetKey((prev) => prev + 1);
  };

  const handleOpenRenameModal = (categoryId: string) => {
    const cat = allCategories.find((c) => c.extId === categoryId) ?? null;
    setCategoryToRename(cat);
    setShowRenameCategoryModal(true);
  };

  const handleCloseRenameModal = () => {
    setShowRenameCategoryModal(false);
    setCategoryToRename(null);
  };

  const handleRenameCategory = async (
    categoryId: string,
    newName: string,
  ): Promise<void> => {
    if (!surveyId) return;

    const original = allCategories.find((c) => c.extId === categoryId);
    if (!original) return;

    // Optimistic update: category name in Redux
    dispatch(
      updateCategoryField({ categoryId, updates: { categoryName: newName } }),
    );

    // Optimistic update: matching map key labels
    // Find the mapkey where referenceId matches the category's extId and category type is POI
    const relatedMapKeys = marketMapKeys.filter(
      (k) => k.referenceId === categoryId && k.category === "POI",
    );
    relatedMapKeys.forEach((mk) => {
      if (mk.mapKeyId) {
        dispatch(updateToggleLabelMapKey({ ...mk, label: newName }));
      }
    });

    try {
      await updatePOICategory({
        surveyId,
        categoryId,
        body: { categoryName: newName },
      }).unwrap();

      // Persist map key label changes to the backend
      for (const mk of relatedMapKeys) {
        if (mk.mapKeyId) {
          await updateSurveyMapKey({
            mapKeyId: mk.mapKeyId,
            mapKey: { label: newName },
            surveyId: surveyId || "",
          }).unwrap();
        }
      }

      setShowRenameCategoryModal(false);
      setCategoryToRename(null);
    } catch (renameError) {
      console.error("Failed to rename category", renameError);
      // Roll back optimistic updates
      dispatch(
        updateCategoryField({
          categoryId,
          updates: { categoryName: original.categoryName },
        }),
      );
      relatedMapKeys.forEach((mk) => {
        if (mk.mapKeyId) {
          dispatch(updateToggleLabelMapKey(mk));
        }
      });
    }
  };

  const handleChange = (field: keyof SurveyPOIRequest, value: string) => {
    const nextValues: SurveyPOIRequest = { ...formValues, [field]: value };

    if (field === "latitude") {
      setCoordinates([coordinates[0], value]);
      updateCoordinateErrors(value, coordinates[0]);
      renderMapPoints(coordinates[0], value, {
        address1: formValues.addressLine1,
        city: formValues.city,
        stateProvince: formValues.state,
        postalCode: formValues.postalCode,
      });
    }

    if (field === "longitude") {
      setCoordinates([value, coordinates[1]]);
      updateCoordinateErrors(coordinates[1], value);
      renderMapPoints(value, coordinates[1], {
        address1: formValues.addressLine1,
        city: formValues.city,
        stateProvince: formValues.state,
        postalCode: formValues.postalCode,
      });
    }

    if (
      field === "addressLine1" ||
      field === "city" ||
      field === "state" ||
      field === "postalCode"
    ) {
      debouncedGeocodeAddress(nextValues);
    }

    setFormValues(nextValues);
  };

  return (
    <div className="flex flex-col">
      {/* Sticky header — stays pinned to the top of the side-sheet scroll area */}
      <div className="sticky top-0 z-10 bg-white px-4 md:px-6 pt-6 pb-4 border-b border-slate-100">
      <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <EmeraldIconButton
          icon="keyboard_arrow_left"
          onClick={handleBackToList}
          type="Icon"
        />
        <div className="w-full max-w-[280px]">
          <CategorySelectWithRename
            key={`category-select-${categorySelectResetKey}`}
            label="Category *"
            placeholder="Search or create a category..."
            value={formValues.categoryId || ""}
            options={categoryOptions}
            categories={allCategories}
            onFocus={() => handleFocus("categoryId", currentPOI?.categoryId)}
            onChange={(selected) => handleCategoryChange(selected)}
            onEditCategory={handleOpenRenameModal}
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          {/* POI record navigator */}
          {poiNavTotal > 1 && (
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 bg-white"
              title="Navigate between points of interest"
            >
              <button
                type="button"
                onClick={() => navigateToPOI(poiNavIndex - 1)}
                disabled={!poiHasPrev}
                aria-label="Previous point of interest"
                className="btn-nav-primary group flex items-center justify-center w-7 h-7 rounded-full border border-primary text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary"
              >
                <EmeraldIcon
                  icon="chevron_left"
                  variant="icon"
                  fontsizeInRem={1.1}
                  className="text-primary group-hover:text-white!"
                />
              </button>
              <span className="text-sm text-slate-500 min-w-[3.5rem] text-center select-none">
                {poiNavIndex + 1} of {poiNavTotal}
              </span>
              <button
                type="button"
                onClick={() => navigateToPOI(poiNavIndex + 1)}
                disabled={!poiHasNext}
                aria-label="Next point of interest"
                className="btn-nav-primary group flex items-center justify-center w-7 h-7 rounded-full border border-primary text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-primary"
              >
                <EmeraldIcon
                  icon="chevron_right"
                  variant="icon"
                  fontsizeInRem={1.1}
                  className="text-primary group-hover:text-white!"
                />
              </button>
            </div>
          )}
          <EmeraldButton
            type={EmeraldButtonVariant.TextButton}
            size={EmeraldButtonSize.Medium}
            id="SC-BackToPOIList"
            label="See All Points of Interest"
            onClick={handleBackToList}
            nativeProps={{ type: "button" }}
          />
          <EmeraldIconButton
            icon="close"
            onClick={handleCloseDrawer}
            type="Icon"
          />
        </div>
      </div>

      {/* Category Required Warning Banner */}
      {showWarning && (
        <div
          style={{
            marginTop: "-5px",
          }}
          className="px-4 py-3 border rounded-sm flex items-center gap-2 bg-warning-light border-warning"
        >
          <EmeraldIcon
            icon="warning_amber"
            type="filled"
            fontsizeInRem={1.2}
            className="text-orange-600"
            variant="icon"
          />
          <span
            className="text-warning-dark"
            style={{ fontSize: "0.875rem", fontWeight: 100 }}
          >
            Category is required. This POI will be deleted if closed without
            one.
          </span>
        </div>
      )}

      <div className="warning-message text-data bg-info-alert border-info-icon border flex p-2.5 gap-2 text-sm items-center rounded-sm">
        <EmeraldIcon
          icon="info"
          type="filled"
          variant="icon"
          className="text-info-icon"
        />
        <span>
          Points of interest below will display in this survey only. Edits will
          not update Site IQ project points of interest or Site IQ team data.
        </span>
       </div>{/* end warning-message */}
      </div>{/* end flex flex-col gap-4 */}
      </div>{/* end sticky header */}

      {/* Scrollable form content */}
      <div className="px-4 md:px-6 pt-6 pb-6 flex flex-col gap-y-6">
      <div className="grid grid-flow-row gap-4">
        <h2 className="text-xl text-data">Address</h2>

        <div className="address-mapOverview-container grid grid-flow-row lg:grid-cols-2 gap-4 sm:grid-cols-1">
          <div className="grid gap-3">
            <EmeraldInputV2
              type="text"
              label="Street Address"
              value={getFieldValue(currentPOI?.addressLine1, "addressLine1")}
              size="small"
              className="without-label"
              onChange={(e: BaseSyntheticEvent) => {
                handleInputChange(
                  e as React.ChangeEvent<HTMLInputElement>,
                  "addressLine1",
                );
                handleChange("addressLine1", e.target.value);
              }}
              onFocus={() =>
                handleFocus("addressLine1", currentPOI?.addressLine1)
              }
              onBlur={() => handleBlur("addressLine1")}
              helperText={formErrors.addressLine1}
            />
            <EmeraldInputV2
              value={getFieldValue(currentPOI?.addressLine2, "addressLine2")}
              size="small"
              label="Address Line 2"
              onChange={(e: BaseSyntheticEvent) => {
                handleInputChange(
                  e as React.ChangeEvent<HTMLInputElement>,
                  "addressLine2",
                );
                handleChange("addressLine2", e.target.value);
              }}
              onFocus={() =>
                handleFocus("addressLine2", currentPOI?.addressLine2)
              }
              onBlur={() => handleBlur("addressLine2")}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <EmeraldInputV2
                type="text"
                label="City"
                value={getFieldValue(currentPOI?.city, "city")}
                size="small"
                className="without-label"
                onChange={(e: BaseSyntheticEvent) => {
                  handleInputChange(
                    e as React.ChangeEvent<HTMLInputElement>,
                    "city",
                  );
                  handleChange("city", e.target.value);
                }}
                onFocus={() => handleFocus("city", currentPOI?.city)}
                onBlur={() => handleBlur("city")}
                helperText={formErrors.city}
              />
              <EmeraldInputV2
                type="text"
                label="State"
                value={getFieldValue(currentPOI?.state, "state")}
                size="small"
                className="without-label"
                onChange={(e: BaseSyntheticEvent) => {
                  handleInputChange(
                    e as React.ChangeEvent<HTMLInputElement>,
                    "state",
                  );
                  handleChange("state", e.target.value);
                }}
                onFocus={() => handleFocus("state", currentPOI?.state)}
                onBlur={() => handleBlur("state")}
                helperText={formErrors.state}
              />
              <EmeraldInputV2
                type="text"
                label="Zip Code"
                value={getFieldValue(currentPOI?.postalCode, "postalCode")}
                size="small"
                className="without-label"
                onChange={(e: BaseSyntheticEvent) => {
                  handleInputChange(
                    e as React.ChangeEvent<HTMLInputElement>,
                    "postalCode",
                  );
                  handleChange("postalCode", e.target.value);
                }}
                onFocus={() =>
                  handleFocus("postalCode", currentPOI?.postalCode)
                }
                onBlur={() => handleBlur("postalCode")}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <EmeraldInputV2
                type="number"
                label="Latitude"
                value={getFieldValue(currentPOI?.latitude, "latitude")}
                size="small"
                className="without-label"
                errorText={isLatInvalid ? "Invalid Latitude" : undefined}
                onChange={(e: BaseSyntheticEvent) => {
                  handleInputChange(
                    e as React.ChangeEvent<HTMLInputElement>,
                    "latitude",
                  );
                  handleChange("latitude", e.target.value);
                }}
                onFocus={() => handleFocus("latitude", currentPOI?.latitude)}
                onBlur={() => handleBlur("latitude")}
                helperText={formErrors.latitude}
              />
              <EmeraldInputV2
                type="number"
                label="Longitude"
                value={getFieldValue(currentPOI?.longitude, "longitude")}
                size="small"
                className="without-label"
                errorText={isLngInvalid ? "Invalid Longitude" : undefined}
                onChange={(e: BaseSyntheticEvent) => {
                  handleInputChange(
                    e as React.ChangeEvent<HTMLInputElement>,
                    "longitude",
                  );
                  handleChange("longitude", e.target.value);
                }}
                onFocus={() => handleFocus("longitude", currentPOI?.longitude)}
                onBlur={() => handleBlur("longitude")}
                helperText={formErrors.longitude}
              />
            </div>
          </div>

          <div className="map-overview relative min-h-[190px] rounded-sm overflow-hidden border border-divider-4">
            <div
              id="poi-map-address-container"
              ref={mapAddressContainerRef}
              className="h-full w-full relative"
            >
              {!isValidCoordinatePair(coordinates[0], coordinates[1]) && (
                <MapOverlay />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-flow-row gap-3">
        <h2 className="text-xl text-data">
          Location Notes (Displays in Web Survey only)
        </h2>
        <div className="max-w-[50%]">
          <EmeraldInput
            placeholder="Write a note that will appear in the tool tip about this location"
            type="textarea"
            value={getFieldValue(currentPOI?.locationNotes, "locationNotes")}
            maxLength={500}
            helperText={
              formErrors.locationNotes ||
              `${(formValues.locationNotes || "").length}/500`
            }
            onChange={(e: BaseSyntheticEvent) => {
              handleInputChange(
                e as React.ChangeEvent<HTMLTextAreaElement>,
                "locationNotes",
              );
              handleChange("locationNotes", e.target.value);
            }}
            onFocus={() =>
              handleFocus("locationNotes", currentPOI?.locationNotes)
            }
            onBlur={() => handleBlur("locationNotes")}
          />
        </div>
      </div>

      <PoiDesignForm key={currentPoiId ?? "poi-design"}/>

      {/* Category Creation Modal */}
      <CategoryCreationModal
        open={showCategoryModal}
        onClose={handleCloseCategoryModal}
        onCategoryCreated={handleCategoryCreated}
        surveyId={surveyId || ""}
        existingCategories={categories}
      />

      {/* Rename Category Modal */}
      <RenameCategoryModal
        key={categoryToRename?.extId}
        open={showRenameCategoryModal}
        onClose={handleCloseRenameModal}
        onRename={handleRenameCategory}
        category={categoryToRename}
        existingCategories={allCategories}
        isLoading={isRenamingCategory}
      />
      </div>{/* end scrollable content */}
    </div>
  );
};

export default PoiDetailForm;
